import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Siren, CalendarCheck, BookOpen, ScrollText, Plus, CheckCircle2, AlertTriangle, Clock,
} from 'lucide-react';

const SubHeader = ({ icon: Icon, title, sub }) => (
  <div className="flex items-start gap-2.5 mb-3">
    <div className="p-1.5 rounded-md bg-slate-900 text-white shrink-0">
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <h3 className="text-sm font-bold text-slate-900">{title}</h3>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  </div>
);

const Badge = ({ color, children }) => {
  const map = {
    red: 'bg-red-100 text-red-800 border-red-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    green: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${map[color] || map.slate}`}>
      {children}
    </span>
  );
};

const ComplianceOperations = () => {
  const { token } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // 3.3 Incidents
  const [incidents, setIncidents] = useState([]);
  const [moeConfigured, setMoeConfigured] = useState(false);
  const [newIncident, setNewIncident] = useState({ title: '', description: '', severity: 'P2' });
  const [creatingInc, setCreatingInc] = useState(false);

  // 3.5 Drills
  const [drills, setDrills] = useState([]);

  // 3.12 Lessons learned
  const [lessons, setLessons] = useState([]);
  const [newLesson, setNewLesson] = useState({
    title: '', source: 'incident', finding: '', corrective_action: '', status: 'open',
  });

  // 9.4 Log review
  const [logReview, setLogReview] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [anomalies, setAnomalies] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [inc, dr, ls, lr] = await Promise.all([
        axios.get(`${API}/compliance/incidents`, auth),
        axios.get(`${API}/compliance/drills`, auth),
        axios.get(`${API}/compliance/lessons-learned`, auth),
        axios.get(`${API}/compliance/log-review`, auth),
      ]);
      setIncidents(inc.data.incidents || []);
      setMoeConfigured(!!inc.data.moe_contact_configured);
      setDrills(dr.data.drills || []);
      setLessons(ls.data.lessons || []);
      setLogReview(lr.data);
    } catch (e) {
      // Silent — page may render before token attaches; the explicit actions will toast.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (token) fetchAll(); }, [token, fetchAll]);

  // ─── 3.3 actions ───
  const createIncident = async () => {
    if (!newIncident.title || !newIncident.description) {
      toast.error('Title and description are required');
      return;
    }
    setCreatingInc(true);
    try {
      const r = await axios.post(`${API}/compliance/incidents`, newIncident, auth);
      toast.success(
        r.data.moe_notification_status === 'sent'
          ? 'Incident recorded — MOE notified automatically'
          : 'Incident recorded',
      );
      setNewIncident({ title: '', description: '', severity: 'P2' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create incident');
    } finally {
      setCreatingInc(false);
    }
  };

  const updateIncidentStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/compliance/incidents/${id}`, { status }, auth);
      toast.success(`Incident marked ${status}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  // ─── 3.5 actions ───
  const updateDrill = async (id, status) => {
    try {
      await axios.patch(`${API}/compliance/drills/${id}`, { status }, auth);
      toast.success(`Drill marked ${status}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  // ─── 3.12 actions ───
  const addLesson = async () => {
    if (!newLesson.title || !newLesson.finding) {
      toast.error('Title and finding are required');
      return;
    }
    try {
      await axios.post(`${API}/compliance/lessons-learned`, newLesson, auth);
      toast.success('Lesson recorded');
      setNewLesson({ title: '', source: 'incident', finding: '', corrective_action: '', status: 'open' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record lesson');
    }
  };

  // ─── 9.4 actions ───
  const acknowledgeReview = async () => {
    try {
      await axios.post(`${API}/compliance/log-review`,
        { notes: reviewNotes, anomalies_found: anomalies }, auth);
      toast.success('Weekly log review acknowledged');
      setReviewNotes('');
      setAnomalies(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to acknowledge');
    }
  };

  return (
    <Card className="p-6 shadow-sm" data-testid="im8-operations-card">
      <div className="mb-5 pb-3 border-b border-slate-200">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-md bg-red-50">
            <Siren className="h-4 w-4 text-red-700" />
          </div>
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">
            IM8 Operations — Incidents, Drills, Lessons, Log Review
          </h2>
        </div>
        <p className="text-sm text-slate-500 mt-1.5 ml-8">
          Closes clauses 3.3, 3.5, 3.12 and 9.4 of the MOE security tender.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ──────────── 3.3 Incident Management ──────────── */}
        <div data-testid="incident-mgmt-section">
          <SubHeader icon={Siren} title="3.3 — Incident Management"
            sub={moeConfigured ? 'P1/P2 auto-notify MOE within SLA' : 'Set MOE_INCIDENT_CONTACT_EMAIL in .env to enable auto-notify'} />

          <div className="space-y-2 p-3 rounded-md bg-slate-50 border border-slate-200 mb-3">
            <Input
              placeholder="Incident title"
              value={newIncident.title}
              onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
              data-testid="incident-title-input"
            />
            <Textarea
              placeholder="Description / observed indicators"
              rows={2}
              value={newIncident.description}
              onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
              data-testid="incident-desc-input"
            />
            <div className="flex gap-2">
              <Select value={newIncident.severity} onValueChange={(v) => setNewIncident({ ...newIncident, severity: v })}>
                <SelectTrigger className="w-32" data-testid="incident-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="P1">P1 — Critical</SelectItem>
                  <SelectItem value="P2">P2 — High</SelectItem>
                  <SelectItem value="P3">P3 — Medium</SelectItem>
                  <SelectItem value="P4">P4 — Low</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={createIncident} disabled={creatingInc} className="bg-red-700 hover:bg-red-800 text-white" data-testid="create-incident-button">
                <Plus className="h-4 w-4 mr-1" />Create
              </Button>
            </div>
          </div>

          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {incidents.length === 0 && <p className="text-xs text-slate-400">No incidents yet.</p>}
            {incidents.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-2 p-2 rounded border border-slate-200 text-xs">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Badge color={i.severity === 'P1' ? 'red' : i.severity === 'P2' ? 'amber' : 'slate'}>{i.severity}</Badge>
                    {i.sla_breached && <Badge color="red">SLA BREACH</Badge>}
                    {i.moe_notification_status === 'sent' && <Badge color="green">MOE notified</Badge>}
                    <Badge color={i.status === 'resolved' ? 'green' : 'blue'}>{i.status}</Badge>
                  </div>
                  <p className="font-semibold text-slate-800 truncate">{i.title}</p>
                  <p className="text-slate-500 truncate">{i.description}</p>
                </div>
                {i.status !== 'resolved' && (
                  <Button size="sm" variant="ghost" onClick={() => updateIncidentStatus(i.id, 'resolved')} data-testid={`resolve-incident-${i.id}`}>
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ──────────── 3.5 Quarterly Drills ──────────── */}
        <div data-testid="drills-section">
          <SubHeader icon={CalendarCheck} title="3.5 — Quarterly Drills" sub={`Schedule for ${new Date().getFullYear()}`} />
          <div className="space-y-1.5">
            {drills.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-2 p-2 rounded border border-slate-200 text-xs">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <Badge color="slate">{d.quarter}</Badge>
                    <Badge color={d.status === 'completed' ? 'green' : d.status === 'skipped' ? 'amber' : 'blue'}>
                      {d.status}
                    </Badge>
                  </div>
                  <p className="font-medium text-slate-800 mt-0.5">{d.title}</p>
                  {d.completed_at && <p className="text-slate-500">Done: {new Date(d.completed_at).toLocaleDateString()}</p>}
                </div>
                {d.status !== 'completed' && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => updateDrill(d.id, 'completed')} data-testid={`complete-drill-${d.id}`}>
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => updateDrill(d.id, 'skipped')} data-testid={`skip-drill-${d.id}`}>
                      Skip
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ──────────── 3.12 Lessons Learned ──────────── */}
        <div data-testid="lessons-section">
          <SubHeader icon={BookOpen} title="3.12 — Lessons Learned Register"
            sub={`${lessons.length} entries • ${lessons.filter(l => l.status !== 'done').length} open`} />

          <div className="space-y-2 p-3 rounded-md bg-slate-50 border border-slate-200 mb-3">
            <Input
              placeholder="Title (e.g. 'Improve incident detection')"
              value={newLesson.title}
              onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
              data-testid="lesson-title-input"
            />
            <Textarea
              placeholder="Finding — what did we learn?"
              rows={2}
              value={newLesson.finding}
              onChange={(e) => setNewLesson({ ...newLesson, finding: e.target.value })}
              data-testid="lesson-finding-input"
            />
            <Textarea
              placeholder="Corrective action"
              rows={2}
              value={newLesson.corrective_action}
              onChange={(e) => setNewLesson({ ...newLesson, corrective_action: e.target.value })}
              data-testid="lesson-action-input"
            />
            <div className="flex gap-2">
              <Select value={newLesson.source} onValueChange={(v) => setNewLesson({ ...newLesson, source: v })}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="incident">Incident</SelectItem>
                  <SelectItem value="drill">Drill</SelectItem>
                  <SelectItem value="audit">Audit</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={addLesson} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-lesson-button">
                <Plus className="h-4 w-4 mr-1" />Record
              </Button>
            </div>
          </div>

          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {lessons.length === 0 && <p className="text-xs text-slate-400">No lessons recorded yet.</p>}
            {lessons.map((l) => (
              <div key={l.id} className="p-2 rounded border border-slate-200 text-xs">
                <div className="flex items-center gap-1.5 mb-1">
                  <Badge color="slate">{l.source}</Badge>
                  <Badge color={l.status === 'done' ? 'green' : l.status === 'in_progress' ? 'amber' : 'blue'}>{l.status}</Badge>
                </div>
                <p className="font-semibold text-slate-800">{l.title}</p>
                <p className="text-slate-500">{l.finding}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ──────────── 9.4 Weekly Log Review ──────────── */}
        <div data-testid="log-review-section">
          <SubHeader icon={ScrollText} title="9.4 — Weekly Log Review"
            sub={logReview ? `${logReview.total_events} events in last ${logReview.window_days} days` : ''} />

          {logReview && (
            <>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="p-2.5 rounded border border-slate-200">
                  <p className="text-xs text-slate-500">Last reviewed</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {logReview.last_review
                      ? new Date(logReview.last_review.reviewed_at).toLocaleDateString()
                      : '—'}
                  </p>
                  <div className="mt-1">
                    {logReview.overdue
                      ? <Badge color="amber"><AlertTriangle className="h-3 w-3 mr-0.5 inline" />Overdue</Badge>
                      : <Badge color="green"><Clock className="h-3 w-3 mr-0.5 inline" />Up to date</Badge>}
                  </div>
                </div>
                <div className="p-2.5 rounded border border-slate-200">
                  <p className="text-xs text-slate-500">Failed logins (7d)</p>
                  <p className="text-lg font-bold text-slate-900">{logReview.failed_logins?.length || 0}</p>
                  <p className="text-xs text-slate-500">Lockouts: {logReview.lockouts?.length || 0}</p>
                </div>
              </div>

              <div className="p-2 rounded border border-slate-200 mb-3 text-xs">
                <p className="font-semibold text-slate-700 mb-1">Events by action</p>
                <div className="grid grid-cols-2 gap-1">
                  {Object.entries(logReview.by_action || {}).slice(0, 8).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-slate-600 truncate">{k}</span>
                      <span className="font-semibold text-slate-900">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2 p-3 rounded-md bg-slate-50 border border-slate-200">
                <Label className="text-xs font-semibold text-slate-700">Acknowledge this week's review</Label>
                <Textarea
                  placeholder="Notes (e.g. spike in failed logins from one IP — added to WAF block list)"
                  rows={2}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  data-testid="log-review-notes-input"
                />
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={anomalies} onChange={(e) => setAnomalies(e.target.checked)} data-testid="anomalies-checkbox" />
                  Anomalies found (creates a follow-up lesson)
                </label>
                <Button onClick={acknowledgeReview} className="bg-slate-900 hover:bg-slate-800 text-white w-full" data-testid="ack-log-review-button">
                  <CheckCircle2 className="h-4 w-4 mr-1" />Acknowledge review
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ComplianceOperations;
